/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import React, { useEffect, useState } from 'react'

import { Dialog, Fade } from '@mui/material'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { Link } from 'react-router-dom'

import bgImage from 'assets/images/illustrations/plant-growing.jpeg'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { auth } from 'config/firebase'
import { useMaterialUIController } from 'context/materialUI'
import IllustrationLayout from 'layouts/authentication/components/IllustrationLayout'
import LoadingComponent from 'modules/Loading'
import { ROUTE_PATHS } from 'routes'

function Illustration(): JSX.Element {
	const [controller] = useMaterialUIController()
	const { orgAssets } = controller
	const [emailInput, setEmailInput] = useState<string>('')
	const [passwordInput, setPasswordInput] = useState<string>('')
	const [dialogOpen, setDialogOpen] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)

	const [isMounted, setIsMounted] = useState(true)

	useEffect(() => {
		return () => setIsMounted(false) // Cleanup on unmount
	}, [])

	const handleSignInClick = async () => {
		setLoading(true)
		try {
			await signInWithEmailAndPassword(auth, emailInput, passwordInput)
		} catch (error: any) {
			if (!isMounted) return
			setLoading(false)

			const errorCode = error.code ?? ''
			switch (errorCode) {
				case 'auth/invalid-credential':
					setError('Invalid Password')
					break
				case 'auth/invalid-email':
					setError('Invalid Email')
					break
				default:
					setError('Error Signing In')
			}

			// Only set timeout if still mounted
			if (isMounted) {
				setTimeout(() => {
					if (isMounted) setError(null)
				}, 10000)
			}
		}
	}

	return (
		<IllustrationLayout
			title="Sign In"
			description="Enter your email and password to sign in"
			illustration={orgAssets?.illustration || bgImage}
		>
			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
				<MDBox width={500} height={200} style={{ backgroundColor: '#fff' }}>
					<MDBox
						p={3}
						display={'flex'}
						flexDirection={'column'}
						justifyContent={'center'}
						alignItems={'center'}
						width={'100%'}
						gap={1}
					>
						<MDTypography variant="h4">Sign-Up Unavailable: Invite Required</MDTypography>
						<MDTypography variant="body2" textAlign="center">
							Please ask your organization owner to request your account. To sign up your
							organization, please request a referral from an existing client. For any enquiries
							please email{' '}
							<MDTypography variant="body2" fontStyle="italic">
								info@tazirides.co.uk
							</MDTypography>
						</MDTypography>
					</MDBox>
				</MDBox>
			</Dialog>
			<MDBox
				component="form"
				role="form"
				onSubmit={e => {
					e.preventDefault()
					handleSignInClick()
				}}
			>
				<MDBox mb={2}>
					<MDInput
						type="email"
						label="Email"
						value={emailInput}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailInput(e.target.value)}
						fullWidth
					/>
				</MDBox>
				<MDBox mb={2}>
					<MDInput
						type="password"
						label="Password"
						value={passwordInput}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPasswordInput(e.target.value)}
						fullWidth
					/>
				</MDBox>
				<MDBox mt={4} mb={1}>
					<MDButton
						cvariant="gradient"
						color="secondary"
						size="large"
						fullWidth
						type="submit"
						onClick={handleSignInClick}
					>
						sign in
						{loading && (
							<LoadingComponent type="circular_white" height={22} width={22} speed={0.9} />
						)}
					</MDButton>
				</MDBox>
				{error && (
					<Fade in={!!error} timeout={1000}>
						<MDBox textAlign="center">
							<MDTypography variant="subtitle2" color="error">
								{error}
							</MDTypography>
						</MDBox>
					</Fade>
				)}
				<MDBox mt={3} textAlign="center">
					<MDTypography variant="button" color="text">
						Don&apos;t have an account?{' '}
						<MDTypography
							component={Link}
							onClick={() => setDialogOpen(true)}
							variant="button"
							color="primary"
							fontWeight="medium"
							textGradient
						>
							Sign up
						</MDTypography>
					</MDTypography>
				</MDBox>
				<MDBox textAlign="center">
					<MDTypography
						component={Link}
						to={ROUTE_PATHS.AUTHENTICATION.RESET_PASSWORD}
						variant="button"
						color="secondary"
						fontWeight="medium"
					>
						Forgotten your password?
					</MDTypography>
				</MDBox>
			</MDBox>
		</IllustrationLayout>
	)
}

export default Illustration
